import {
  ACTION_DESKTOP_NAV_HEADER,
  ACTION_DESKTOP_NAV_RESERVATION_TYPE_MENU_DESTINATION,
  CATEGORY_NAV,
} from 'utils/analytics/eventTypesConstants';
import { Button, List, ListItem } from 'omakase';
import { ClickAwayListener, Fade, Paper, Popper } from '@material-ui/core';
import {
  IconArrowDownSmall,
  IconArrowUpSmall,
  IconDeliveryScooter,
  IconEvents,
  IconPrivateDining,
  IconToGoBadge,
} from 'ui/icons';
import {
  IconChevronSmallDown,
  IconChevronSmallUp,
  IconDelivery,
  IconPlate,
  IconTicket,
  IconToGo,
} from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { Theme, css, useTheme } from '@emotion/react';
import { buildPropsForReservationSearchMenuItem } from 'components/site-menu/MobileSiteMenuImpl';
import { consumer } from '@tock/coretypes';
import {
  popupContent,
  shortReservationButton,
} from 'components/header/desktopHeaderNavigateButtonsCssDefs';
import Anchor from 'components/Anchor';
import React from 'react';

interface DesktopHeaderNavigationButtonsProps {
  metroArea?: consumer.IMetroArea;
}

const DesktopHeaderNavigationButtons: React.FC<DesktopHeaderNavigationButtonsProps> = ({
  metroArea,
}) => {
  const [popoverAnchoredToElement, setPopoverAnchoredToElement] =
    React.useState<HTMLElement | null>(null);

  const triggerButtonRef = React.useRef<HTMLButtonElement | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setPopoverAnchoredToElement(event.currentTarget);
    window.analytics?.sendEvent(
      CATEGORY_NAV,
      ACTION_DESKTOP_NAV_HEADER,
      'Menu for reservation type selection opened'
    );
  };
  const handleMenuClose = () => {
    setPopoverAnchoredToElement(null);
  };
  // handles analytics for a MenuItem click, then closes the menu.
  const handleItemClicked = (menuItemName: string) => {
    window.analytics?.sendEvent(
      CATEGORY_NAV,
      ACTION_DESKTOP_NAV_RESERVATION_TYPE_MENU_DESTINATION,
      menuItemName
    );
    handleMenuClose();
  };
  const computeHrefForType = (type: consumer.OfferingSearchType) =>
    buildPropsForReservationSearchMenuItem(type, metroArea).href;

  const isMenuOpen = !!popoverAnchoredToElement;

  const computeMenuIcon = () =>
    isMenuOpen ? (
      <IconMigrationUtil legacyIcon={<IconArrowUpSmall />} newIcon={<IconChevronSmallUp />} />
    ) : (
      <IconMigrationUtil legacyIcon={<IconArrowDownSmall />} newIcon={<IconChevronSmallDown />} />
    );
  const reservationButtonAriaLabel = 'Book a reservation. Toggle open a menu of reservation types';
  const standardAnimationTime = parseInt(useTheme().timings.duration.standard, 10);

  // We apply the same escape-key listener to the button and to the Popper component.
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleMenuClose();
      window?.setTimeout(() => triggerButtonRef.current?.focus(), 0);
    }
  };

  return (
    <>
      <Button
        aria-haspopup
        aria-label={reservationButtonAriaLabel}
        iconEnd={computeMenuIcon()}
        size="large"
        variant="ghost"
        onClick={handleMenuOpen}
        css={(theme: Theme) => shortReservationButton(theme, isMenuOpen)}
      >
        Reservations
      </Button>
      <Popper
        open={isMenuOpen}
        anchorEl={popoverAnchoredToElement}
        disablePortal
        placement="bottom-start"
        transition
        onKeyDown={onKeyDown}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleMenuClose}>
            <Fade {...TransitionProps} timeout={standardAnimationTime}>
              <Paper
                css={({ shadow }: Theme) => css`
                  border-radius: 0;
                  box-shadow: ${shadow[100]};
                  min-width: 308px;
                `}
              >
                <List
                  /* After navigating to the user's intended target, the menu should close. */
                  css={popupContent}
                >
                  <ListItem
                    component={Anchor}
                    elementStart={
                      <IconMigrationUtil
                        legacyIcon={<IconPrivateDining />}
                        newIcon={<IconPlate />}
                      />
                    }
                    href={computeHrefForType('DINE_IN_EXPERIENCES')}
                    variant="basic"
                    size="large"
                    weight="medium"
                    onClick={() => handleItemClicked('dine-in')}
                  >
                    Reservations
                  </ListItem>
                  <ListItem
                    component={Anchor}
                    elementStart={
                      <IconMigrationUtil
                        legacyIcon={<IconDeliveryScooter />}
                        newIcon={<IconDelivery />}
                      />
                    }
                    href={computeHrefForType('ORDER_DELIVERY')}
                    variant="basic"
                    size="large"
                    weight="medium"
                    onClick={() => handleItemClicked('delivery')}
                  >
                    Delivery
                  </ListItem>
                  <ListItem
                    component={Anchor}
                    elementStart={
                      <IconMigrationUtil legacyIcon={<IconToGoBadge />} newIcon={<IconToGo />} />
                    }
                    href={computeHrefForType('ORDER_PICKUP')}
                    variant="basic"
                    size="large"
                    weight="medium"
                    onClick={() => handleItemClicked('pickup')}
                  >
                    Pickup
                  </ListItem>
                  <ListItem
                    component={Anchor}
                    elementStart={
                      <IconMigrationUtil legacyIcon={<IconEvents />} newIcon={<IconTicket />} />
                    }
                    href="/collections/events-all"
                    variant="basic"
                    size="large"
                    weight="medium"
                    onClick={() => handleItemClicked('events')}
                  >
                    Events
                  </ListItem>
                </List>
              </Paper>
            </Fade>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
};

export default DesktopHeaderNavigationButtons;
